import { Navigate, useLocation } from 'react-router-dom';

import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import { CAMINHO_ENTRADA, destinoDoUsuario } from './destino';
import { type Papel } from './papeis';
import { useAuth } from './useAuth';

interface RotaProtegidaProps {
  children: React.ReactNode;
  /**
   * Papeis que podem entrar. Sem a lista, basta estar autenticado — e o caso de
   * tela que todo usuario tem, como a propria conta.
   */
  papeis?: readonly Papel[];
  /**
   * Guarda de uma TELA dentro de uma area ja liberada, e nao da area inteira.
   *
   * Existe porque SME e DRE dividem a mesma area (`AREA_SME`) e nem toda tela
   * dela e dos dois papeis (`ItemDeNavegacao.papeis`). Sem esta guarda, o item
   * sumia do menu mas a rota continuava respondendo: quem acessa como DRE abria
   * `/sme/revisoes` pela URL e via os pedidos de todas as diretorias.
   *
   * Com o caminho informado, a recusa aparece dentro da casca, com volta para o
   * inicio da area — a pessoa continua no portal dela, so nao nesta tela.
   */
  voltarPara?: string;
}

/**
 * Rota protegida por papel, com redirecionamento amigavel.
 *
 * docs/AUTENTICACAO-E-ACESSO.md, secao 6: a interface e a primeira das tres
 * camadas. Aqui ela decide o que aparece; a API e o banco cobram de novo, e nao
 * confiam nesta decisao.
 *
 * Tres situacoes, tres tratamentos diferentes — e a diferenca importa:
 *
 * - nao autenticado: vai para a entrada, guardando de onde veio, para voltar
 *   depois. Nao e erro, e nao mostra cara de erro.
 * - autenticado sem papel do TEG: fica onde esta e ve o que fazer. Mandar para a
 *   entrada seria laco: entrar de novo daria no mesmo.
 * - autenticado com papel errado: ve que esta area nao e dele, com caminho de
 *   volta para a area que e.
 */
export function RotaProtegida({ children, papeis, voltarPara }: RotaProtegidaProps) {
  const { estado, usuario, aviso, sair } = useAuth();
  const local = useLocation();

  if (estado === 'iniciando' || estado === 'entrando') {
    return (
      <div role="status" aria-live="polite">
        <Spinner fullPage size="lg" />
        <p className="sr-only">Verificando seu acesso.</p>
      </div>
    );
  }

  if (estado === 'sem-acesso') {
    return (
      <ErrorState
        fullPage
        variant="forbidden"
        title="Sua conta ainda não tem acesso ao sistema"
        description={
          aviso ??
          'Seu cadastro existe, mas ainda não tem um perfil de acesso. Procure o administrador do sistema do TEG.'
        }
        primaryAction={{ label: 'Sair', onClick: () => void sair() }}
      />
    );
  }

  if (estado === 'anonimo' || !usuario) {
    // O caminho pretendido viaja na URL da entrada, e a tela de entrada o
    // devolve ao fluxo. Nada de estado escondido: recarregar a pagina de entrada
    // nao pode perder o destino.
    const voltar = encodeURIComponent(`${local.pathname}${local.search}`);
    return <Navigate to={`${CAMINHO_ENTRADA}?voltar=${voltar}`} replace />;
  }

  const temPapelExigido =
    !papeis || papeis.length === 0 || papeis.some((papel) => usuario.papeis.includes(papel));

  if (!temPapelExigido && voltarPara) {
    return (
      <ErrorState
        variant="forbidden"
        description="Seu perfil não alcança esta tela. Se você precisa deste acesso, fale com o administrador do TEG."
        primaryAction={{ label: 'Voltar ao início', to: voltarPara }}
      />
    );
  }

  if (!temPapelExigido) {
    const casa = destinoDoUsuario(usuario);

    return (
      <ErrorState
        fullPage
        variant="forbidden"
        description="Seu perfil não alcança esta área do sistema. Se você precisa deste acesso, fale com o administrador do TEG."
        primaryAction={{ label: 'Ir para a minha área', to: casa }}
        // Sair, e nao "entrar com outra conta": a sessao do Keycloak continua
        // valida, entao entrar de novo devolveria a MESMA conta sem perguntar
        // nada. Para trocar de conta, primeiro se sai.
        secondaryAction={{ label: 'Sair', onClick: () => void sair() }}
      />
    );
  }

  return <>{children}</>;
}
